/**
 * Type-safe API client for roster operations.
 *
 * Provides:
 * - Type-safe methods for all API endpoints
 * - Session ID injection via X-Session-Id header
 * - Error handling
 * - Request/response transformation
 */

import { clearEditGrant } from '@/utils/editGrant';
import type {
  GenerateRosterRequest,
  RosterState,
  HistorySnapshot,
  OperationResult,
  SeatFirstCountersResult,
  GenerateRosterResult,
  AddOfficersRequest,
  AddMainOfficerRequest,
  MainOfficerTemplateResponse,
  SwapCountersRequest,
  MoveCountersRequest,
  DeleteOfficerRequest,
  RemoveOfficerRequest,
  ManualAllocationsRequest,
  UpdateOfficerAssignmentsRequest,
  UpdateAvailabilityRequest,
  UnavailablePeriod,
  AddUnavailabilityRequest,
  AddBatchUnavailabilityRequest,
  RemoveUnavailabilityRequest,
  DebugMatrices,
  ParseTextResponse,
  ParsePdfBase64Request,
  OperationMode,
  ContactList,
  TemplateMeta,
  OTPresetsConfig,
  GenerationDefaultsConfig,
  SOSDefaultsConfig,
  AllAdminConfigs,
  BreakRulesConfig,
  HWDefaultsConfig,
  ShiftConfig,
  PriorityOrderConfig,
  TemplateValidateResponse,
  TemplateSnapshot,
  QuickActionsConfig,
  BatchCounterOpsRequest,
  CounterOpConflict,
  TemplateExportSource,
  TemplateGrid,
  TemplateGridSaveRequest,
  ConfigSetKind,
  ConfigSetList,
  AdoptConfigSetResult,
  Note,
  BrowseSession,
  NoReliefAck,
  NoReliefAckKind,
  CallAck,
  ChopsSummary,
  ChopsSession,
  ChopsCheck,
  ChopsHandoff,
  ChopsShift,
  DutyOption,
  OTBreakStarts,
  OtColorSettingsPayload,
} from '@/types/api';
import type { ClickEventPayload } from '@/utils/clickTracker';
import { getMaxCounterDuration } from '@/utils/maxCounterDuration';

/**
 * Base API configuration
 */
const API_BASE = '/api';

/**
 * Stamp the user's "max ctr duration" onto a request that can trigger an
 * automatic break assignment.
 *
 * Applied here rather than at each call site: every add, regenerate, fill and
 * finish-generation has to carry it, and the one that forgets is the one that
 * silently plans the roster to a cap the user never chose.
 */
function withMaxCounterDuration<T extends object>(request: T): T & { max_counter_duration: string } {
  return { ...request, max_counter_duration: getMaxCounterDuration() };
}


/**
 * Module-level session ID and user name, set synchronously by SessionContext.
 */
let _sessionId: string | null = null;
let _userName: string | null = null;

export function setClientSessionId(id: string | null) {
  _sessionId = id;
}

export function setClientUserName(name: string | null) {
  _userName = name;
}

/**
 * Identifies this browser tab for the lifetime of the page. Sent as X-Client-Id
 * on every request; the server stamps it on the snapshot broadcast an edit
 * causes, so this tab can recognise its own echo — and the same user's edits
 * made in another tab or device are not mistaken for it.
 */
const _clientId: string = (() => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* fall through */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
})();

export function getClientId(): string {
  return _clientId;
}

/**
 * Numbers this tab's writes in the order they are sent (X-Op-Seq). Every snapshot the
 * server broadcasts says, per tab, the newest write of theirs it includes (`_seqs`),
 * and a write's own response carries its number too (see opSeqOf) — so the roster
 * state can tell which of its local, not-yet-confirmed edits a given snapshot already
 * holds, and never paints an older snapshot over a newer edit.
 */
let _opSeq = 0;
const _inFlightSeqs = new Set<number>();
/** The number of the last write this tab sent (0 before any). */
export function lastOpSeq(): number {
  return _opSeq;
}
/** The newest write number at or below which every write has been answered. */
export function settledOpSeq(): number {
  let min = Infinity;
  for (const seq of _inFlightSeqs) min = Math.min(min, seq);
  return min === Infinity ? _opSeq : min - 1;
}
const OP_SEQ = Symbol('opSeq');
/** The X-Op-Seq a write's response belongs to, or null for a read / unknown object. */
export function opSeqOf(result: unknown): number | null {
  return result && typeof result === 'object' ? ((result as { [OP_SEQ]?: number })[OP_SEQ] ?? null) : null;
}

/**
 * Edit mode, set synchronously by SessionContext. Every session opens
 * view-only; while it is, any request that would change something is refused
 * here, before it leaves the browser — see `apiFetch`.
 */
let _canEdit = false;

export function setClientCanEdit(canEdit: boolean) {
  _canEdit = canEdit;
}

/**
 * Server-side proof of edit rights for the CURRENT session — minted by
 * /sessions/verify-pin once the shared PIN checks out, and required (see
 * api/main.py's enforce_edit_token) on every write. `_canEdit` above is what
 * gates the UI and stops an honest browser from even trying; this is what
 * actually gets checked, since nothing stops a request built by hand from
 * skipping the client-side gate entirely.
 */
let _editToken: string | null = null;

export function setClientEditToken(token: string | null) {
  _editToken = token;
}

/**
 * Edit tokens for sessions other than the current one — minted by the
 * generate wizard's own endpoints (generateRoster, publishGeneration) for the
 * draft/forked session they hand back, and used by sessionHeaderFor below.
 * Registering happens automatically inside those two calls; nothing else
 * needs to touch this.
 */
const _draftEditTokens = new Map<string, string>();

function registerEditToken(sessionId: string | undefined, token: string | null | undefined) {
  if (sessionId && token) _draftEditTokens.set(sessionId, token);
}

/** The token SessionContext should carry over when switching to `sessionId` —
 *  e.g. after publishing a generation draft the tab is about to move onto. */
export function getRegisteredEditToken(sessionId: string): string | null {
  return _draftEditTokens.get(sessionId) ?? (sessionId === _sessionId ? _editToken : null);
}

export const VIEW_ONLY_MESSAGE =
  "You're in View mode. To make changes, switch to Edit mode with the toggle at the top right of the Home page (PIN required).";

/** Thrown in place of a write attempted while the tab is view-only. */
export class ViewOnlyError extends Error {
  constructor() {
    super(VIEW_ONLY_MESSAGE);
    this.name = 'ViewOnlyError';
  }
}

/** Told whenever a write is refused for view-only, so the app can say why and
 *  roll back anything the caller already drew optimistically. */
let _onViewOnlyBlocked: (() => void) | null = null;
export function setViewOnlyBlockedNotifier(fn: (() => void) | null) {
  _onViewOnlyBlocked = fn;
}

/**
 * For UI code that blocks an edit before it ever reaches apiFetch — a custom
 * clickable element that can't just be given `disabled`, e.g. a canvas
 * drag-select or a counter-label popup. Fires the same notice a refused
 * network write would, without needing a network call to trigger it.
 */
export function notifyViewOnlyBlocked() {
  _onViewOnlyBlocked?.();
}

/**
 * Non-GET endpoints that only read or compute — they change nothing, so a
 * view-only tab may still call them. Everything else that isn't a GET is a write.
 */
const READ_ONLY_WRITE_METHOD_ENDPOINTS = new Set([
  '/sessions/new',
  '/sessions/join',
  '/sessions/verify-pin',
  '/errors/client',
  '/analytics/clicks',
  '/roster/stats_filtered',
  '/roster/parse-pdf-b64',
  '/officers/preview_counter_ops',
]);

function isWriteRequest(endpoint: string, options?: RequestInit): boolean {
  const method = (options?.method ?? 'GET').toUpperCase();
  if (method === 'GET' || method === 'HEAD') return false;
  // Chops sessions belong to no ORCA session, so there is no edit mode or token
  // to gate them with — access is by URL (see api/routers/chops.py).
  if (endpoint.split('?')[0].startsWith('/chops')) return false;
  // The contact list is global too — any user may upload a new one.
  if (endpoint.split('?')[0].startsWith('/contacts')) return false;
  return !READ_ONLY_WRITE_METHOD_ENDPOINTS.has(endpoint.split('?')[0]);
}

/** Called once when a user-triggered request (non-GET) hits a 502 and is about to retry. */
let _onApiRetry: (() => void) | null = null;
export function setApiRetryNotifier(fn: (() => void) | null) {
  _onApiRetry = fn;
}

/**
 * Base64-encode a file's bytes, in chunks.
 *
 * The obvious one-liner — reducing the byte array into a string with `+=` —
 * builds a million-character string one character at a time and spends most of
 * a second doing it on the main thread for a roster PDF, with the tab frozen
 * throughout. Chunking through `String.fromCharCode.apply` is the same result
 * in a fraction of the time. 0x8000 keeps each call's argument list well inside
 * the engine's stack limit.
 */
function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunks: string[] = [];
  for (let i = 0; i < bytes.length; i += 0x8000) {
    chunks.push(String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000))));
  }
  return btoa(chunks.join(''));
}

/**
 * Send one request against a different session than the client is on.
 *
 * Used for the generate wizard's remaining steps: a regeneration writes into a
 * draft session the user has NOT been switched to, so those calls have to name
 * their target explicitly rather than inheriting the client's session.
 */
function sessionHeaderFor(sessionId?: string): { headers?: Record<string, string> } {
  if (!sessionId) return {};
  const editToken = getRegisteredEditToken(sessionId);
  return {
    headers: {
      'X-Session-Id': sessionId,
      ...(editToken ? { 'X-Edit-Token': editToken } : {}),
    },
  };
}

// A request that just hangs (a dropped WiFi/cellular handoff, a background
// Safari tab throttled mid-fetch — both routine on iPad) never rejects on its
// own: fetch() has no built-in timeout. Without one, a caller's try/catch
// never runs, loading spinners never clear, and the user sees a button that
// "does nothing" with no error to explain why. 45s is generous enough for a
// real roster generation, short enough that a genuinely dead connection
// surfaces well within a support conversation.
const REQUEST_TIMEOUT_MS = 45_000;

function isReadRequest(options?: RequestInit): boolean {
  return (options?.method ?? 'GET').toUpperCase() === 'GET';
}

/**
 * Generic fetch wrapper with error handling and session header injection.
 * Automatically retries gateway errors during a deployment (502/503 always,
 * 504 for reads only) with a short backoff before giving up. Also times out a
 * hung request so every call is guaranteed to eventually resolve or reject.
 */

// Set for the duration of a synchronous call by foldIntoPrevious: the write it sends continues the
// user's last action, so the server folds its version into that action's.
let _foldNext = false;

/**
 * Send the write `send` makes as a continuation of the user's previous action: one action is one
 * version in the history (and so one undo), however many requests it took. `send` must start its
 * request synchronously, as every apiClient call does.
 */
export function foldIntoPrevious<T>(send: () => Promise<T>): Promise<T> {
  _foldNext = true;
  try {
    return send();
  } finally {
    _foldNext = false;
  }
}

async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit,
  _attempt = 0
): Promise<T> {
  if (_foldNext) options = { ...options, headers: { ...(options?.headers as Record<string, string> | undefined), 'X-Fold-Into-Previous': '1' } };
  if (!_canEdit && isWriteRequest(endpoint, options)) {
    _onViewOnlyBlocked?.();
    throw new ViewOnlyError();
  }

  const opSeq = isWriteRequest(endpoint, options) ? ++_opSeq : null;
  if (opSeq !== null) _inFlightSeqs.add(opSeq);
  try {
    return await sendRequest<T>(endpoint, options, _attempt, opSeq);
  } finally {
    if (opSeq !== null) _inFlightSeqs.delete(opSeq);
  }
}

async function sendRequest<T>(
  endpoint: string,
  options: RequestInit | undefined,
  _attempt: number,
  opSeq: number | null,
): Promise<T> {
  const sessionHeaders: Record<string, string> = {
    ...(_sessionId ? { 'X-Session-Id': _sessionId } : {}),
    ...(_userName ? { 'X-User-Name': _userName } : {}),
    ...(_editToken ? { 'X-Edit-Token': _editToken } : {}),
    'X-Client-Id': _clientId,
    ...(opSeq !== null ? { 'X-Op-Seq': String(opSeq) } : {}),
  };

  // Skip Content-Type for FormData — browser must set it with the multipart boundary
  const isFormData = options?.body instanceof FormData;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    // `headers` is built AFTER options are spread, not before. The other way round,
    // any caller passing headers of its own replaced the whole merged object —
    // taking Content-Type with it, so a JSON body arrived at the API as a string.
    response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      signal: options?.signal ?? controller.signal,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...sessionHeaders,
        ...options?.headers,
      },
    });
  } catch (err) {
    // A read can safely be repeated, so a dropped connection or a request that
    // hung through a deploy is retried quietly instead of surfacing an error.
    // Writes are not: a timed-out write may still have landed.
    if (isReadRequest(options) && !options?.signal && _attempt < 2) {
      await new Promise(resolve => window.setTimeout(resolve, _attempt === 0 ? 1500 : 3000));
      return sendRequest<T>(endpoint, options, _attempt + 1, opSeq);
    }
    if ((err as Error).name === 'AbortError') {
      throw new Error('The server is taking longer than usual to respond. Your work is safe — please try again in a moment.');
    }
    throw new Error("Couldn't reach the server. Please check your connection and try again.");
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    // 502/503 means the proxy couldn't reach the backend (container restart/deploy):
    // the request never reached the app, so retrying is always safe. 504 is
    // different — the backend got the request and was too slow to answer, so a
    // write may already have landed and repeating it would apply it twice. Only
    // reads are retried on 504.
    const gatewayDown = response.status === 502 || response.status === 503;
    const gatewayTimeout = response.status === 504 && isReadRequest(options);
    if ((gatewayDown || gatewayTimeout) && _attempt < 3) {
      // Notify only on the first retry of a user-triggered (non-GET) request.
      if (_attempt === 0 && (options?.method ?? 'GET') !== 'GET') {
        _onApiRetry?.();
      }
      const delay = [2000, 4000, 6000][_attempt];
      await new Promise(resolve => window.setTimeout(resolve, delay));
      return sendRequest<T>(endpoint, options, _attempt + 1, opSeq);
    }
    const error = await response.json().catch(() => ({
      error: 'Request failed',
      detail: response.statusText || `HTTP ${response.status}`,
    }));
    // The server-side half of view-only mode refused this write — happens when
    // the client's own local canEdit is stale (a token that expired while the
    // tab stayed open) rather than the normal case (blocked pre-flight above).
    // Clear it locally too, so the UI catches up without waiting on a reload.
    if (response.status === 403 && error.error_code === 'view_only') {
      _canEdit = false;
      _editToken = null;
      clearEditGrant(_sessionId);          // the server no longer honours it: do not restore it into the next tab
      _onViewOnlyBlocked?.();
      throw new ViewOnlyError();
    }
    const rawDetail = error.detail || error.error || `HTTP ${response.status} from ${endpoint}`;
    const detail = typeof rawDetail === 'string' ? rawDetail : JSON.stringify(rawDetail);
    throw new Error(`[${response.status}] ${detail}`);
  }

  const result = await response.json();
  if (opSeq !== null && result && typeof result === 'object') {
    Object.defineProperty(result, OP_SEQ, { value: opSeq });
  }
  return result;
}

// ============================================================
// Session API types
// ============================================================

export interface NewSessionResponse {
  session_id: string;
  user_id: number;
}

export interface JoinSessionResponse {
  session_id: string;
  user_id: number;
  has_state: boolean;
}

export interface VerifyPinResponse {
  valid: boolean;
  /** Set only when valid — the caller passes it to setCanEdit so it's persisted. */
  edit_token?: string | null;
}

export interface ClientErrorReport {
  message: string;
  stack?: string;
  component_stack?: string;
  url?: string;
}

/**
 * API Client
 */
export const apiClient = {
  // ============================================================
  // Session Operations
  // ============================================================

  sessions: {
    /**
     * Create a new session. Returns the generated session_id and user_id.
     */
    create: (userName: string): Promise<NewSessionResponse> =>
      apiFetch('/sessions/new', {
        method: 'POST',
        body: JSON.stringify({ user_name: userName }),
      }),

    /**
     * Join an existing session by ID.
     */
    join: (sessionId: string, userName: string): Promise<JoinSessionResponse> =>
      apiFetch('/sessions/join', {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId, user_name: userName }),
      }),

    /**
     * Sessions at least a day old, openable view-only without their ID.
     */
    browse: (): Promise<BrowseSession[]> => apiFetch('/sessions/browse'),

    /**
     * Check the edit PIN, scoped to `sessionId`. Returns an edit token on
     * success — SessionContext is what actually stores it (setCanEdit).
     */
    verifyPin: (pin: string, sessionId: string): Promise<VerifyPinResponse> =>
      apiFetch('/sessions/verify-pin', {
        method: 'POST',
        body: JSON.stringify({ pin, session_id: sessionId }),
      }),
  },

  // ============================================================
  // Error Reporting
  // ============================================================

  errors: {
    /**
     * Report an error caught in the browser (ErrorBoundary or a global
     * handler), tagged with the current session so it can be looked up
     * later instead of asking the user to reproduce it. Never throws —
     * a failed report shouldn't cause a second error.
     */
    report: (report: ClientErrorReport): Promise<{ success: boolean }> =>
      apiFetch<{ success: boolean }>('/errors/client', {
        method: 'POST',
        body: JSON.stringify(report),
      }).catch(() => ({ success: false })),
  },

  // ============================================================
  // Click analytics
  // ============================================================

  analytics: {
    /** Send a batch of button clicks. Never throws — tracking must not break the app. */
    sendClicks: (events: ClickEventPayload[], keepalive = false): Promise<{ success: boolean }> =>
      apiFetch<{ success: boolean }>('/analytics/clicks', {
        method: 'POST',
        body: JSON.stringify({ events }),
        keepalive,
      }).catch(() => ({ success: false })),
  },

  // ============================================================
  // No Relief call log
  // ============================================================

  noRelief: {
    listAcks: (): Promise<NoReliefAck[]> => apiFetch('/no-relief/acks'),
    addAck: (counter: number, slot: number, kind: NoReliefAckKind): Promise<NoReliefAck> =>
      apiFetch('/no-relief/acks', {
        method: 'POST',
        body: JSON.stringify({ counter, slot, kind }),
      }),
    /** Undo a tick — only the latest one for its counter/slot; see backend/db_handlers.py. */
    deleteAck: (ackId: number): Promise<{ success: boolean }> =>
      apiFetch(`/no-relief/acks/${ackId}`, { method: 'DELETE' }),
  },

  // ============================================================
  // Officer phone-call reminders
  // ============================================================

  callAcks: {
    list: (): Promise<CallAck[]> => apiFetch('/call-acks'),
    /** Mark the officer's blocks (by signature) as phoned about, or re-arm them. Returns the full log. */
    set: (officerId: string, signatures: string[], called: boolean): Promise<CallAck[]> =>
      apiFetch('/call-acks', {
        method: 'POST',
        body: JSON.stringify({ officer_id: officerId, signatures, called }),
      }),
  },

  // ============================================================
  // Roster Operations
  // ============================================================

  /**
   * Generate a new roster.
   *
   * Returns the session the roster landed in, which is a NEW one whenever
   * `forked` is set — regenerating forks rather than rewriting. Callers must
   * switch the client to it before making any further request.
   */
  generateRoster: async (request: GenerateRosterRequest): Promise<GenerateRosterResult> => {
    const result = await apiFetch<GenerateRosterResult>('/roster/generate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    // The wizard's remaining steps write into this (possibly new) session
    // before the client ever switches to it — register the token now so
    // sessionHeaderFor can find it.
    registerEditToken(result.session_id, result.edit_token);
    return result;
  },

  parsePdfFile: async (file: File, operationMode?: OperationMode): Promise<ParseTextResponse> => {
    // Send as base64 JSON to avoid multipart/form-data proxy restrictions.
    const bytes = await file.arrayBuffer();
    const req: ParsePdfBase64Request = { file_b64: toBase64(bytes), operation_mode: operationMode ?? null };
    return apiFetch('/roster/parse-pdf-b64', { method: 'POST', body: JSON.stringify(req) });
  },

  /**
   * Get current roster state
   */
  /** The lean state a tab holds: version headers, not every version's grid (see
   *  utils/statePatch and utils/versionStore). */
  getState: (): Promise<RosterState> =>
    apiFetch('/roster/state?lite=1'),

  /** One retained version, by id. A version never changes; see utils/versionStore. */
  getVersion: (vid: string): Promise<HistorySnapshot> =>
    apiFetch(`/roster/versions/${encodeURIComponent(vid)}`),

  getDebugMatrices: (): Promise<DebugMatrices> =>
    apiFetch('/roster/debug'),

  /**
   * Manning Stats text scoped to a subset of officers and/or a time window — same
   * layout as the unfiltered stats. `officer_keys: undefined` means every officer;
   * `[]` is a real filter that includes nobody.
   */
  getFilteredStats: (request: { officer_keys?: string[]; start_time?: string; end_time?: string }): Promise<{ stats: string }> =>
    apiFetch('/roster/stats_filtered', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  // ============================================================
  // Officer Operations
  // ============================================================

  /**
   * Add SOS/OT officers
   */
  addOfficers: (request: AddOfficersRequest, sessionOverride?: string): Promise<OperationResult> =>
    apiFetch('/officers/add', {
      method: 'POST',
      body: JSON.stringify(withMaxCounterDuration(request)),
      ...sessionHeaderFor(sessionOverride),
    }),

  /**
   * Swap all officer assignments between two counters
   */
  swapCounters: (request: SwapCountersRequest): Promise<OperationResult> =>
    apiFetch('/officers/swap_counters', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  /**
   * Move all officer assignments from one counter to another (one-way)
   */
  /**
   * Apply several counter moves/swaps as ONE undoable edit.
   *
   * Not a client-side loop over swapCounters/moveCounters: the backend reads
   * every op against the pre-batch roster (so rotations work) and records a
   * single history entry (so the batch undoes in one step).
   */
  batchCounterOps: (request: BatchCounterOpsRequest): Promise<OperationResult> =>
    apiFetch('/officers/batch_counter_ops', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  /**
   * Which destinations a batch would land on that already have officers.
   * Checked against the server's state, so the prompt can't be based on a stale
   * copy of the grid. Writes nothing.
   */
  previewCounterOps: (request: BatchCounterOpsRequest): Promise<{ conflicts: CounterOpConflict[] }> =>
    apiFetch('/officers/preview_counter_ops', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  moveCounters: (request: MoveCountersRequest): Promise<OperationResult> =>
    apiFetch('/officers/move_counters', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  /**
   * Delete officer assignments
   */
  deleteOfficer: (request: DeleteOfficerRequest, sessionOverride?: string): Promise<OperationResult> =>
    apiFetch('/officers/delete', {
      method: 'POST',
      body: JSON.stringify(request),
      ...sessionHeaderFor(sessionOverride),
    }),

  /**
   * Completely remove an officer and all their data from the roster
   */
  removeOfficer: (request: RemoveOfficerRequest, sessionOverride?: string): Promise<OperationResult> =>
    apiFetch('/officers/remove', {
      method: 'POST',
      body: JSON.stringify(request),
      ...sessionHeaderFor(sessionOverride),
    }),

  /**
   * Completely remove several officers in one call (one history entry, one state rebuild)
   */
  removeOfficers: (officers: string[], sessionOverride?: string): Promise<OperationResult> =>
    apiFetch('/officers/remove_batch', {
      method: 'POST',
      body: JSON.stringify({ officers }),
      ...sessionHeaderFor(sessionOverride),
    }),

  /**
   * Regenerate an SOS officer's breaks and counter assignment from scratch
   */
  regenerateOfficer: (request: {
    officer_key: string; custom_break_sizes?: number[]; break_earliest?: string; break_latest_end?: string;
    cleared_break_slots?: number[]; user_break_slots?: number[];
    /** This officer's assignments to clear first, in the same edit — "clear and
     *  regenerate" is one version to undo, not one per range plus one for the fill. */
    clear_ranges?: Array<{ start_time: string; end_time: string }>;
  }): Promise<OperationResult> =>
    apiFetch('/officers/regenerate', {
      method: 'POST',
      body: JSON.stringify(withMaxCounterDuration(request)),
    }),

  /**
   * Jointly auto-assign the unassigned slots of several SOS/OT officers in one
   * shared counter-assignment pass (shared optimum across officers, not per-officer
   * local optima). Existing assignments are preserved; the batch is one history entry.
   */
  smartFillBatch: (request: {
    officer_keys: string[];
    /** Name/deployment edits to apply as part of this same batch — one history entry
     *  instead of a separate metadata-update version. */
    metadata_changes?: Record<string, { name: string; deployment: string }>;
    /** Per officer, per vacant-window break sizes, aligned by vacant-block order. */
    custom_break_sizes_map?: Record<string, number[][]>;
    /** Per officer, the vacant-window block indices to fill (aligned by vacant-block order).
     *  Absent officer → all their windows are filled. */
    window_selection?: Record<string, number[]>;
    /** Per officer, slot indices the user erased in the grid — treated as available even if
     *  shorter than the break threshold. */
    cleared_break_slots?: Record<string, number[]>;
    /** Per officer, slot indices the user explicitly marked as break — never filled. */
    user_break_slots?: Record<string, number[]>;
    /** Counter assignments to clear before the fill runs, as part of the same batch — so
     *  freeing up time and re-planning it are one undoable version, not one per officer. */
    clear_ranges?: Array<{ officer_key: string; start_time: string; end_time: string }>;
  }): Promise<OperationResult> =>
    apiFetch('/officers/smart_fill_batch', {
      method: 'POST',
      body: JSON.stringify(withMaxCounterDuration(request)),
    }),

  /**
   * Clear several assignment ranges, across any number of officers, as ONE edit:
   * one request, one history version, one broadcast. (The clear-only form of the
   * smart-fill batch — no officers to fill, only time to free.)
   */
  clearRanges: (
    ranges: Array<{ officer_key: string; start_time: string; end_time: string }>,
    sessionOverride?: string,
  ): Promise<OperationResult> =>
    apiFetch('/officers/smart_fill_batch', {
      method: 'POST',
      body: JSON.stringify({ officer_keys: [], clear_ranges: ranges }),
      ...sessionHeaderFor(sessionOverride),
    }),

  /**
   * Manual counter allocations
   */
  manualAllocate: (request: ManualAllocationsRequest, sessionOverride?: string): Promise<OperationResult> =>
    apiFetch('/officers/allocate', {
      method: 'POST',
      body: JSON.stringify(request),
      ...sessionHeaderFor(sessionOverride),
    }),

  /**
   * Finish a generation draft: hand it the current session's users, write the
   * new ID onto the old session's version, and announce it to the room.
   * Called once the user completes step 4 of the wizard — not before.
   */
  publishGeneration: async (draftSessionId: string): Promise<GenerateRosterResult> => {
    const result = await apiFetch<GenerateRosterResult>('/roster/publish-generation', {
      method: 'POST',
      body: JSON.stringify({ draft_session_id: draftSessionId }),
    });
    // Re-registered (generateRoster already did once) in case this session's
    // token expired between the two calls on a long wizard session.
    registerEditToken(result.session_id, result.edit_token);
    return result;
  },

  /**
   * Mark where the generate wizard finished with this roster. Everything up to
   * here is the generation; anything after it is an edit, and edits are what
   * make the next regeneration fork to a new session rather than rebuild here.
   */
  finishGeneration: (sessionOverride?: string): Promise<OperationResult> =>
    apiFetch('/roster/finish-generation', {
      method: 'POST',
      body: JSON.stringify(withMaxCounterDuration({})),
      ...sessionHeaderFor(sessionOverride),
    }),

  /** Throw away a generation draft the user closed the wizard on. */
  discardGeneration: (draftSessionId: string): Promise<OperationResult> =>
    apiFetch('/roster/discard-generation', {
      method: 'POST',
      body: JSON.stringify({ draft_session_id: draftSessionId }),
    }),

  /**
   * Atomically update one officer's assignments with a single history entry.
   * Replaces the separate delete + allocate calls in the edit popup save flow.
   */
  updateOfficerAssignments: (request: UpdateOfficerAssignmentsRequest): Promise<OperationResult> =>
    apiFetch('/officers/update_assignments', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  /**
   * Update officer availability window (visual markers only)
   */
  updateAvailability: (request: UpdateAvailabilityRequest): Promise<OperationResult> =>
    apiFetch('/officers/availability', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  /** Put a main officer back on the printed roster (the PDF's counters) for the whole shift — not an auto-assign. */
  restoreOfficer: (request: { officer_key: string }): Promise<OperationResult> =>
    apiFetch('/officers/restore', { method: 'POST', body: JSON.stringify(request) }),

  /**
   * Batch re-assign partially-assigned officers in a single history entry.
   * Replaces the 3-call-per-officer pattern (delete + updateAvailability + regenerate).
   */
  /**
   * Seat main officers' first stints (already cleared) on a free counter — the first free for
   * the whole stint, else from the earliest slot one is free for at least 30 minutes of it.
   */
  seatFirstCounters: (
    request: { officers: Array<{ officer_key: string; start: string; end: string }> },
    sessionOverride?: string,
  ): Promise<SeatFirstCountersResult> =>
    apiFetch('/officers/seat_first_counters', {
      method: 'POST',
      body: JSON.stringify(request),
      ...sessionHeaderFor(sessionOverride),
    }),

  /** Give each main officer's vacancy (the closing GL stint, say) one counter free for all of it, before SOS are added. */
  seatVacancies: (sessionOverride?: string): Promise<OperationResult> =>
    apiFetch('/officers/seat_vacancies', { method: 'POST', ...sessionHeaderFor(sessionOverride) }),

  reassignPartialOfficers: (request: {
    officers: Array<{
      officer_key: string;
      original_start: string;
      original_end: string;
      avail_start: string;
      avail_end: string;
      segments?: Array<{ start: string; end: string }>;
      custom_break_sizes?: number[];
    }>;
  }, sessionOverride?: string): Promise<OperationResult> =>
    apiFetch('/officers/reassign_partials', {
      method: 'POST',
      body: JSON.stringify(withMaxCounterDuration(request)),
      ...sessionHeaderFor(sessionOverride),
    }),

  /**
   * Compute default break pattern for a given timing + shift.
   * Single source of truth — reads the same admin break_rules config as the scheduler.
   */
  getBreakPattern: (params: { timing: string; officer_type?: 'sos' | 'ot'; shift?: string }): Promise<{ pattern: number[]; avail_slots: number }> =>
    apiFetch(`/officers/break-pattern?${new URLSearchParams({
      timing: params.timing,
      officer_type: params.officer_type ?? 'sos',
      shift: params.shift ?? 'morning',
      // The first break rule starts just past the cap, so the preview must use the same one.
      max_counter_duration: getMaxCounterDuration(),
    }).toString()}`),

  /** The break start times the OT form lists for one window — the scheduler's own rule. */
  getOTBreakStarts: (params: { start: string; end: string; shift: string; stays_until?: string; break_slots?: number[] }): Promise<OTBreakStarts> =>
    apiFetch(`/officers/ot-break-starts?${new URLSearchParams({
      start: params.start,
      end: params.end,
      shift: params.shift,
      ...(params.stays_until ? { stays_until: params.stays_until } : {}),
      ...(params.break_slots?.length ? { break_slots: params.break_slots.join(',') } : {}),
    }).toString()}`),

  // ============================================================
  // Counter Availability Operations
  // ============================================================

  /**
   * Add counter unavailability
   */
  addUnavailability: (request: AddUnavailabilityRequest): Promise<OperationResult> =>
    apiFetch('/roster/unavailability/add', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  /**
   * Add unavailability for multiple counters at once
   */
  addUnavailabilityBatch: (request: AddBatchUnavailabilityRequest, sessionOverride?: string): Promise<OperationResult> =>
    apiFetch('/roster/unavailability/add_batch', {
      method: 'POST',
      body: JSON.stringify(request),
      ...sessionHeaderFor(sessionOverride),
    }),

  /**
   * Remove counter unavailability
   */
  removeUnavailability: (request: RemoveUnavailabilityRequest): Promise<OperationResult> =>
    apiFetch('/roster/unavailability/remove', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  /**
   * Get unavailability periods
   */
  getUnavailability: (): Promise<UnavailablePeriod[]> =>
    apiFetch('/roster/unavailability'),

  /**
   * Get this session's counter priority-order config for the current mode/shift.
   * Per-session (scoped by session_id via the X-Session-Id header), not an admin setting.
   */
  getPriorityOrder: (): Promise<PriorityOrderConfig> =>
    apiFetch('/roster/priority-order'),

  /**
   * Persist this session's counter priority-order config for the current mode/shift.
   */
  setPriorityOrder: (value: PriorityOrderConfig): Promise<OperationResult> =>
    apiFetch('/roster/priority-order', {
      method: 'PUT',
      body: JSON.stringify(value),
    }),

  /**
   * Whether this session's auto-assign may hand a counter over with a one-slot overlap.
   * Per-session (scoped by session_id via the X-Session-Id header), not an admin setting.
   */
  getHandoffOverlap: (): Promise<{ allow_handoff_overlap: boolean }> =>
    apiFetch('/roster/handoff-overlap'),

  /**
   * Turn this session's one-slot handoff overlap on or off. Affects the next
   * auto-assign; nothing already on the board is re-planned.
   */
  setHandoffOverlap: (allow_handoff_overlap: boolean): Promise<OperationResult> =>
    apiFetch('/roster/handoff-overlap', {
      method: 'PUT',
      body: JSON.stringify({ allow_handoff_overlap }),
    }),

  /** This session's end-of-shift HOTO notes (officer key -> text). */
  getHotoNotes: (): Promise<{ hoto_notes: Record<string, string> }> =>
    apiFetch('/roster/hoto-notes'),

  /** Set (blank clears) one officer's HOTO note — shared with everyone in the session. */
  setHotoNote: (officer_key: string, note: string): Promise<OperationResult> =>
    apiFetch('/roster/hoto-notes', {
      method: 'PUT',
      body: JSON.stringify({ officer_key, note }),
    }),

  /** Set the cell colour of each usual OT timing for this session (null: the built-in colours). */
  setOtColors: (settings: OtColorSettingsPayload | null): Promise<OperationResult> =>
    apiFetch('/roster/ot-colors', {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    }),

  /** Replace the free-text duties this session offers in its counter dropdowns — shared with everyone in it.
   *  `clearRemoved` also takes every officer off the duties the list drops (one undoable edit); without it
   *  the server refuses to drop a duty somebody is on. */
  setDutyOptions: (options: DutyOption[], clearRemoved = false): Promise<OperationResult> =>
    apiFetch('/roster/duty-options', {
      method: 'PUT',
      body: JSON.stringify({ options, clear_removed: clearRemoved }),
    }),

  /** Draw Main/SOS officers in the other type's colour (null: their own). Colour only, shared with
   *  everyone in the session. A list is one write, so a whole run of S/Ns changes at once. */
  setOfficerColour: (officer: string | string[], colour_as: 'MAIN' | 'SOS' | null): Promise<OperationResult> =>
    apiFetch('/roster/officer-colour', {
      method: 'PUT',
      body: JSON.stringify(Array.isArray(officer) ? { officer_keys: officer, colour_as } : { officer_key: officer, colour_as }),
    }),

  /** This session's officer row order (empty = the default order). */
  getOfficerOrder: (): Promise<{ officer_order: string[] }> =>
    apiFetch('/roster/officer-order'),

  /** Set the officer row order for this session — shared with everyone in it. */
  setOfficerOrder: (officer_order: string[]): Promise<OperationResult> =>
    apiFetch('/roster/officer-order', {
      method: 'PUT',
      body: JSON.stringify({ officer_order }),
    }),

  /** This session's officer-card WhatsApp footer (null = nobody has changed the default). */
  getWhatsAppFooter: (): Promise<{ whatsapp_footer: string | null }> =>
    apiFetch('/roster/whatsapp-footer'),

  /** Set the footer for this session — anyone in the session may. */
  setWhatsAppFooter: (whatsapp_footer: string): Promise<OperationResult> =>
    apiFetch('/roster/whatsapp-footer', {
      method: 'PUT',
      body: JSON.stringify({ whatsapp_footer }),
    }),

  /**
   * Get pre-defined counter schedule template for a Main officer SN
   */
  getMainOfficerTemplate: (snNumber: number): Promise<MainOfficerTemplateResponse> =>
    apiFetch(`/roster/main-officer-template/${snNumber}`),

  /**
   * Add a Main officer with a specific SN and manual counter assignments
   */
  addMainOfficer: (request: AddMainOfficerRequest): Promise<OperationResult> =>
    apiFetch('/officers/add-main', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  /**
   * Restore a specific history snapshot as the new current state (non-destructive).
   * @param historyIndex 0-based index into state.history array
   * @param action Optional action label: 'undo', 'redo', or 'restore'
   */
  /** Used for undo/redo/restore-from-history. Returns the full post-restore state
   * directly — apply it locally with applySnapshot() instead of following up with
   * a separate refetch(), which would cost a second network round trip and a second,
   * redundant state-construction on the server for no benefit. */
  restoreRoster: (historyIndex: number, action?: string): Promise<RosterState> =>
    apiFetch('/roster/restore?lite=1', {
      method: 'POST',
      body: JSON.stringify({ history_index: historyIndex, ...(action ? { action } : {}) }),
    }),

  /** Undo: move the version pointer back one step (non-destructive). Returns the full
   *  post-undo state so the caller can apply it directly. */
  undoRoster: (): Promise<RosterState> =>
    apiFetch('/roster/undo?lite=1', { method: 'POST' }),

  /** Redo: move the version pointer forward one step after an undo. */
  redoRoster: (): Promise<RosterState> =>
    apiFetch('/roster/redo?lite=1', { method: 'POST' }),

  /**
   * Get the list of SN numbers already in the roster (to compute unused SNs for the UI).
   */
  getUsedSns: (): Promise<{ used_sns: number[] }> =>
    apiFetch('/officers/used-sns'),

  /**
   * The officer contact list (all teams) and when it was last uploaded.
   * Empty, with no timestamp, until someone uploads a workbook.
   */
  getContacts: (): Promise<ContactList> =>
    apiFetch('/contacts'),

  /** Replace the whole contact list with an uploaded .xlsx (one sheet per team). */
  uploadContacts: (file: File): Promise<ContactList> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiFetch('/contacts/upload', { method: 'POST', body: formData });
  },

  // ============================================================
  // Session Notes (shared sticky-notes board)
  // ============================================================

  getNotes: (): Promise<Note[]> =>
    apiFetch('/notes'),

  createNote: (text: string = '', afterId: number | null = null): Promise<Note> =>
    apiFetch('/notes', { method: 'POST', body: JSON.stringify({ text, after_id: afterId }) }),

  /** `keepalive` lets the request finish after the page is gone — for a save sent as the tab closes or reloads. */
  updateNote: (noteId: number, updates: { text?: string; locked?: boolean; color?: string; pinned?: boolean }, opts?: { keepalive?: boolean }): Promise<Note> =>
    apiFetch(`/notes/${noteId}`, { method: 'PATCH', body: JSON.stringify(updates), ...(opts?.keepalive ? { keepalive: true } : {}) }),

  deleteNote: (noteId: number): Promise<{ success: boolean }> =>
    apiFetch(`/notes/${noteId}`, { method: 'DELETE' }),

  // ============================================================
  // Roster Template Operations
  // ============================================================

  /**
   * Download all four mode/shift templates as a single Excel blob.
   */
  exportTemplate: async (source: TemplateExportSource = 'live'): Promise<Blob> => {
    const sessionHeaders: Record<string, string> = {
      ...(_sessionId ? { 'X-Session-Id': _sessionId } : {}),
      ...(_userName ? { 'X-User-Name': _userName } : {}),
    };
    const response = await fetch(`${API_BASE}/templates/export?source=${source}`, { headers: sessionHeaders });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(err.detail || `HTTP ${response.status}`);
    }
    return response.blob();
  },

  /**
   * Check an uploaded workbook without saving anything.
   * Returns every problem found plus a preview of what applying it would change.
   */
  validateTemplate: async (file: File): Promise<TemplateValidateResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiFetch('/templates/validate', { method: 'POST', body: formData });
  },

  /** Apply a workbook already checked by validateTemplate(), using its token. */
  applyTemplate: (token: string): Promise<{ success: boolean; message: string; saved_tabs: string[]; snapshot_id: number }> =>
    apiFetch('/templates/apply', { method: 'POST', body: JSON.stringify({ token }) }),

  /** One mode/shift template as raw per-slot values, for the in-app grid editor. */
  getTemplateGrid: (mode: OperationMode, shift: string): Promise<TemplateGrid> =>
    apiFetch(`/templates/grid?mode=${encodeURIComponent(mode)}&shift=${encodeURIComponent(shift)}`),

  /** Save a grid edited in the browser. Snapshotted like an upload. */
  putTemplateGrid: (request: TemplateGridSaveRequest): Promise<{ success: boolean; message: string; snapshot_id: number }> =>
    apiFetch('/templates/grid', { method: 'PUT', body: JSON.stringify(request) }),

  /** Rollback points, newest first. */
  getTemplateSnapshots: (): Promise<{ snapshots: TemplateSnapshot[] }> =>
    apiFetch('/templates/snapshots'),

  /** Restore a previous template state. The current state is snapshotted first. */
  rollbackTemplate: (snapshotId: number): Promise<{ success: boolean; message: string }> =>
    apiFetch('/templates/rollback', { method: 'POST', body: JSON.stringify({ snapshot_id: snapshotId }) }),

  /**
   * Return metadata for the current template (officer count, counter defs, etc.)
   */
  getTemplateMeta: (mode: OperationMode, shift: string): Promise<TemplateMeta> =>
    apiFetch(`/templates/meta?mode=${encodeURIComponent(mode)}&shift=${encodeURIComponent(shift)}`),

  /**
   * Reset all templates to hardcoded defaults by removing any custom DB records.
   */
  resetTemplate: (): Promise<{ success: boolean; message: string; records_deleted: number }> =>
    apiFetch('/templates/reset', { method: 'POST' }),

  getTemplateStorageInfo: (): Promise<{ persistent: boolean; type: string; warning: string | null }> =>
    apiFetch('/templates/storage-info'),

  // ============================================================
  // Admin Configuration Operations
  // ============================================================

  adminConfig: {
    getAll: (): Promise<AllAdminConfigs> =>
      apiFetch('/admin/config/all'),

    getSOSDefaults: (): Promise<SOSDefaultsConfig> =>
      apiFetch('/admin/config/sos_defaults'),

    setSOSDefaults: (value: SOSDefaultsConfig): Promise<{ success: boolean }> =>
      apiFetch('/admin/config/sos_defaults', {
        method: 'PUT',
        body: JSON.stringify(value),
      }),

    getOTPresets: (): Promise<OTPresetsConfig> =>
      apiFetch('/admin/config/ot_presets'),

    setOTPresets: (value: OTPresetsConfig): Promise<{ success: boolean }> =>
      apiFetch('/admin/config/ot_presets', {
        method: 'PUT',
        body: JSON.stringify(value),
      }),

    getGenerationDefaults: (): Promise<GenerationDefaultsConfig> =>
      apiFetch('/admin/config/generation_defaults'),

    setGenerationDefaults: (value: GenerationDefaultsConfig): Promise<{ success: boolean }> =>
      apiFetch('/admin/config/generation_defaults', {
        method: 'PUT',
        body: JSON.stringify(value),
      }),

    getBreakRules: (): Promise<BreakRulesConfig> =>
      apiFetch('/admin/config/break_rules'),

    setBreakRules: (value: BreakRulesConfig): Promise<{ success: boolean }> =>
      apiFetch('/admin/config/break_rules', {
        method: 'PUT',
        body: JSON.stringify(value),
      }),

    getHWDefaults: (): Promise<HWDefaultsConfig> =>
      apiFetch('/admin/config/hw_defaults'),

    setHWDefaults: (value: HWDefaultsConfig): Promise<{ success: boolean }> =>
      apiFetch('/admin/config/hw_defaults', {
        method: 'PUT',
        body: JSON.stringify(value),
      }),

    getShiftConfig: (): Promise<ShiftConfig> =>
      apiFetch('/admin/config/shift_config'),

    setShiftConfig: (value: ShiftConfig): Promise<{ success: boolean }> =>
      apiFetch('/admin/config/shift_config', {
        method: 'PUT',
        body: JSON.stringify(value),
      }),

    getQuickActions: (): Promise<QuickActionsConfig> =>
      apiFetch('/admin/config/quick_actions'),

    setQuickActions: (value: QuickActionsConfig): Promise<{ success: boolean }> =>
      apiFetch('/admin/config/quick_actions', {
        method: 'PUT',
        body: JSON.stringify(value),
      }),

  },

  // ============================================================
  // Saved Configuration Sets
  // ============================================================

  configSets: {
    /** This session's own sets of a kind, plus anything shared with it. */
    list: (kind: ConfigSetKind): Promise<ConfigSetList> =>
      apiFetch(`/config-sets/${kind}`),

    /**
     * Freeze the current working configuration under a name.
     *
     * Generation sets are otherwise only created by a generation and named after
     * the session it produced, which is no use to someone who wants to hand
     * their configuration to a colleague.
     */
    save: (kind: ConfigSetKind, name: string, shared = false): Promise<{ id: number }> =>
      apiFetch(`/config-sets/${kind}/save`, {
        method: 'POST',
        body: JSON.stringify({ name, shared }),
      }),

    /** Offer a set to other sessions' pickers, or take it back off them. */
    share: (id: number, shared: boolean): Promise<{ shared: boolean; in_use_by: string[] }> =>
      apiFetch(`/config-sets/${id}/share`, {
        method: 'POST',
        body: JSON.stringify({ shared }),
      }),

    /**
     * Start using a set. A shortcut set switches over immediately; a generation
     * set loads into settings and reaches a roster only through the next
     * generation, so nothing on screen moves.
     */
    adopt: (id: number): Promise<AdoptConfigSetResult> =>
      apiFetch(`/config-sets/${id}/adopt`, { method: 'POST' }),
  },
};

/**
 * Chops sessions — the HO/TO form. Independent of any ORCA session: no
 * X-Session-Id is needed (apiFetch only adds one when a session is joined).
 */
export const chopsApi = {
  list: (duty_date?: string): Promise<ChopsSummary[]> =>
    apiFetch(`/chops${duty_date ? `?duty_date=${encodeURIComponent(duty_date)}` : ''}`),

  /** The roster Excel (or a PDF). Date and shift are read off it; chops correct them on the form. */
  create: async (
    file: File,
    ho: Record<string, string> = {},
    blocked: string[] = [],
    clear_tags = false,
    is_default = true,
  ): Promise<ChopsSession> => {
    const file_b64 = toBase64(await file.arrayBuffer());
    return apiFetch('/chops', {
      method: 'POST',
      body: JSON.stringify({ file_b64, ho, blocked, clear_tags, is_default }),
    });
  },

  get: (id: number): Promise<ChopsSession> => apiFetch(`/chops/${id}`),

  update: (
    id: number,
    changes: Partial<Pick<ChopsSession, 'ho' | 'blocked' | 'moves' | 'overrides' | 'label' | 'duty_date' | 'shift_type'>>,
  ): Promise<ChopsSession> =>
    apiFetch(`/chops/${id}`, { method: 'PATCH', body: JSON.stringify(changes) }),

  /**
   * Build the tagged roster on the server ahead of the download, so it is ready when the HOTO page
   * image is — the two are slow for different reasons and need not wait on each other.
   */
  preparePdf: (id: number): Promise<void> =>
    fetch(`${API_BASE}/chops/${id}/pdf/prepare`, { method: 'POST' }).then(() => undefined, () => undefined),

  /**
   * The roster with re-seated officers' new counters written in and the HOTO page attached. The page
   * is sent as the raw PNG (no base64: a third smaller and nothing to encode or decode).
   */
  downloadPdf: async (id: number, hotoPng: Blob | null): Promise<Blob> => {
    const response = await fetch(`${API_BASE}/chops/${id}/pdf`, {
      method: 'POST',
      headers: hotoPng ? { 'Content-Type': 'image/png' } : undefined,
      body: hotoPng ?? undefined,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(err.detail || `HTTP ${response.status}`);
    }
    return response.blob();
  },

  /**
   * The roster workbook chops uploaded, with the re-seated officers' new counters written beside their
   * names and nothing else changed — so it stays an editable Excel. The file name (and .xlsx / .xlsm)
   * comes from the server.
   */
  downloadXlsx: async (id: number): Promise<{ blob: Blob; filename: string }> => {
    const response = await fetch(`${API_BASE}/chops/${id}/xlsx`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(err.detail || `HTTP ${response.status}`);
    }
    const named = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '');
    return { blob: await response.blob(), filename: named?.[1] ?? 'HOTO.xlsx' };
  },

  setDefault: (id: number, is_default: boolean): Promise<ChopsSummary> =>
    apiFetch(`/chops/${id}/default`, { method: 'POST', body: JSON.stringify({ is_default }) }),

  remove: (id: number): Promise<{ success: boolean }> =>
    apiFetch(`/chops/${id}`, { method: 'DELETE' }),

  /** The default session's handoff, which the car page continues from. Omit the date for the most recent. */
  getDefault: (shift_type: ChopsShift, duty_date?: string): Promise<ChopsHandoff> =>
    apiFetch(
      `/chops/default?shift_type=${shift_type}${duty_date ? `&duty_date=${encodeURIComponent(duty_date)}` : ''}`,
    ),

  /** A session's handoff again, by id — to re-read it, or to see whether chops changed it since. */
  handoff: (id: number): Promise<ChopsHandoff> => apiFetch(`/chops/${id}/handoff`),

  /**
   * Is the car page's copy still the one to use? One cheap call for the whole date and shift: the same
   * session unchanged or changed, replaced by another default, no longer the default, or deleted.
   */
  check: (id: number, shift_type: ChopsShift, duty_date: string, version: string): Promise<ChopsCheck> =>
    apiFetch(
      `/chops/check?id=${id}&shift_type=${shift_type}&duty_date=${encodeURIComponent(duty_date)}` +
      `&version=${encodeURIComponent(version)}`,
    ),

  /** A corrected roster (the Excel workbook, or a PDF) into this session, keeping everything chops decided. */
  replaceRoster: async (id: number, file: File, clear_tags?: boolean): Promise<ChopsSession> => {
    const file_b64 = toBase64(await file.arrayBuffer());
    return apiFetch(`/chops/${id}/roster`, { method: 'POST', body: JSON.stringify({ file_b64, clear_tags }) });
  },

  /** Dates (newest first) that have a default for this shift. */
  defaultDates: (shift_type: ChopsShift): Promise<string[]> =>
    apiFetch(`/chops/default/dates?shift_type=${shift_type}`),
};

